import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { takeUrlToken } from "@/lib/urlToken";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const R = {
  en: { title: "New password", hint: "Choose a new password (8 characters minimum). All your other sessions will be signed out.", pw: "New password", pw2: "Confirm password", mismatch: "Passwords don't match", save: "Save new password", done: "Password updated — log in with your new password.", invalid: "This link is invalid or has expired.", again: "Request a new link", back: "← Back to login" },
  fr: { title: "Nouveau mot de passe", hint: "Choisis un nouveau mot de passe (8 caractères minimum). Tes autres sessions seront déconnectées.", pw: "Nouveau mot de passe", pw2: "Confirme le mot de passe", mismatch: "Les mots de passe ne correspondent pas", save: "Enregistrer", done: "Mot de passe mis à jour — connecte-toi avec ton nouveau mot de passe.", invalid: "Ce lien est invalide ou a expiré.", again: "Demander un nouveau lien", back: "← Retour à la connexion" },
};

export default function ResetPassword() {
  const { lang } = useLang();
  const r = R[lang] || R.en;
  const [token] = useState(takeUrlToken);
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState(!token);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error(r.mismatch);
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success(r.done);
      nav("/auth?mode=login");
    } catch (err) {
      if (err?.response?.status === 400 && /link/i.test(err.response.data?.detail || "")) setInvalid(true);
      else toast.error(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const inputCls = "mt-1 bg-[#090C10] border-[#1E2638] text-white";
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10" data-testid="reset-page">
      <div className="w-full max-w-md rounded-2xl border border-[#1E2638] bg-[#121721] p-8">
        <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">DLSPORT · SÉCURITÉ</div>
        <h1 className="font-display font-black uppercase text-3xl mt-2" data-testid="reset-title">{r.title}</h1>
        {invalid ? (
          <div className="mt-4" data-testid="reset-invalid">
            <p className="text-sm text-[#FF2E54]">{r.invalid}</p>
            <Link to="/forgot-password" className="inline-block mt-4 text-sm text-[#00F076] hover:underline" data-testid="reset-request-again">{r.again}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-3">
            <p className="text-sm text-slate-400">{r.hint}</p>
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{r.pw}</Label>
              <Input required type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" className={inputCls} />
            </div>
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{r.pw2}</Label>
              <Input required type="password" minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" className={inputCls} />
            </div>
            <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">{loading ? "…" : r.save}</Button>
          </form>
        )}
        <div className="mt-6 text-center">
          <Link to="/auth?mode=login" className="text-sm text-slate-400 hover:text-white" data-testid="reset-back">{r.back}</Link>
        </div>
      </div>
    </div>
  );
}
